import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setUser(userData);
    return userData;
  };

  const loginWithToken = async (newToken, userData) => {
    // Direct login with token (for trial accounts)
    localStorage.setItem("token", newToken);
    setToken(newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, phone, password) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email, 
      phone, 
      password 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  // Plan hierarchy: prueba < estandar < premium < vip
  const getPlanLevel = () => {
    const planType = user?.plan_type?.toLowerCase();
    if (planType === "vip") return 3;
    if (planType === "premium" || planType === "advanced") return 2;
    if (planType === "estandar" || planType === "standard") return 1;
    return 0; // prueba (trial)
  };

  const isTrialMode = () => getPlanLevel() === 0;
  const isStandard = () => getPlanLevel() >= 1;
  const isPremium = () => getPlanLevel() >= 2; // Premium or VIP
  const isVip = () => getPlanLevel() === 3;
  
  // Check specific feature access
  const hasFeature = (feature) => {
    const level = getPlanLevel();
    const features = {
      // Estándar features (level 1+)
      "whatsapp": 1,
      "ai_help": 1,
      "documents": 1,
      "questions": 1,
      "notifications": 1,
      // Premium features (level 2+)
      "chat_asesor": 2,
      "interview_simulator": 2,
      "optimization": 2,
      "priority_support": 2,
      // VIP features (level 3)
      "zoom_direct": 3,
      "dedicated_officer": 3,
      "express_process": 3,
    };
    return level >= (features[feature] || 999);
  };

  const getPlanFeatures = () => {
    return user?.plan_features || {};
  };
  
  const getPlanName = () => {
    const planType = user?.plan_type?.toLowerCase();
    if (planType === "vip") return "VIP Enterprise";
    if (planType === "premium" || planType === "advanced") return "Premium";
    if (planType === "prueba") return "Plan Prueba";
    return "Estándar";
  };

  // Trial mode specific helpers
  const getTrialDemoData = () => {
    // Pre-loaded demo data for trial users
    return {
      demoName: "María García López",
      demoProgress: 67,
      demoProbability: 78,
      demoStep: 4,
      demoDocuments: 5,
      demoInterviewDate: "15 de Febrero, 2026",
      demoMessages: 3,
      demoNotifications: 2
    };
  };

  // Check if user can perform trial-restricted action
  const canPerformAction = (action) => {
    if (!isTrialMode()) return true;
    
    // Actions allowed in trial mode (view-only)
    const allowedActions = [
      "view_dashboard",
      "view_documents",
      "view_progress",
      "view_probability",
      "navigate",
      "view_help"
    ];
    
    return allowedActions.includes(action);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login,
      loginWithToken,
      register, 
      logout, 
      fetchUser,
      isPremium,
      isStandard,
      isVip,
      isTrialMode,
      hasFeature,
      getPlanFeatures,
      getPlanName,
      getPlanLevel,
      getTrialDemoData,
      canPerformAction
    }}>
      {children}
    </AuthContext.Provider>
  );
};
